import { useState, useMemo } from 'react';
import { Search, Pause, Play, AlertCircle, ShieldAlert, Check, X, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { CRMDialogContent } from '../../crm/components/CRMDialogContent';
import { Button } from '../../components/ui/button';
import { managerAPI } from './useManagerData';
import { apiError } from '../../crm/context/CRMAuthContext';

const CATEGORY_MAP = {
  all: 'Tümü',
  forex: 'Forex',
  crypto: 'Kripto',
  stocks_bist: 'BIST',
  stocks_us: 'ABD',
  stocks_eu: 'Avrupa',
  commodities: 'Emtia',
  indices: 'Endeks',
};

export const InstrumentControlModal = ({ instruments = [], activeControls = [], onClose, onUpdated }) => {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [selectedSymbol, setSelectedSymbol] = useState(instruments[0]?.symbol || '');
  const [halted, setHalted] = useState(true);
  const [reason, setReason] = useState('Piyasa oynaklığı / kurum kararı');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Halted symbols lookup
  const haltedMap = useMemo(() => {
    const map = {};
    for (const ctrl of activeControls) {
      if (ctrl.kind === 'instrument' && ctrl.halted) {
        map[ctrl.target] = ctrl.reason || 'Durduruldu';
      }
    }
    return map;
  }, [activeControls]);

  // Filter instruments
  const filteredInstruments = useMemo(() => {
    const q = search.trim().toLowerCase();
    return instruments.filter(inst => {
      const matchCat = category === 'all' || 
        inst.market_type === category || 
        (category === 'stocks_eu' && inst.market_type?.startsWith('stocks_') && inst.market_type !== 'stocks_us' && inst.market_type !== 'stocks_bist');
      if (!matchCat) return false;
      if (!q) return true;
      return (
        inst.symbol?.toLowerCase().includes(q) ||
        inst.name?.toLowerCase().includes(q) ||
        inst.market_type?.toLowerCase().includes(q)
      );
    });
  }, [instruments, search, category]);

  const selectedInstrument = useMemo(() => {
    return instruments.find(i => i.symbol === selectedSymbol);
  }, [instruments, selectedSymbol]);

  const isCurrentlyHalted = Boolean(haltedMap[selectedSymbol]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSymbol) {
      setError('Lütfen bir enstrüman seçin.');
      return;
    }

    setBusy(true);
    setError('');

    try {
      await managerAPI.post('/sessions/control', {
        kind: 'instrument',
        target: selectedSymbol,
        halted,
        reason: reason.trim() || undefined,
      });
      toast.success(`${selectedSymbol} seans kontrolü kaydedildi (${halted ? 'Durduruldu' : 'İşlemler Açıldı'}).`);
      await onUpdated?.();
      onClose();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleQuickResume = async (symbol) => {
    setBusy(true);
    try {
      await managerAPI.post('/sessions/control', {
        kind: 'instrument',
        target: symbol,
        halted: false,
        reason: 'Durdurma kaldırıldı',
      });
      toast.success(`${symbol} için durdurma kaldırıldı.`);
      await onUpdated?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => { if (!open && !busy) onClose(); }}>
      <CRMDialogContent className="crm-dialog instrument-control-dialog" data-testid="manager-instrument-control-modal" style={{ maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Pause size={20} color="#dc2626" />
            <DialogTitle>Enstrüman Seans &amp; İşlem Kontrolü</DialogTitle>
          </div>
          <DialogDescription>
            Belirli bir enstrüman için yeni pozisyon açılışlarını durdurun veya tekrar işleme açın.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="crm-error" role="alert" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '6px', background: '#fee2e2', color: '#991b1b', fontSize: '13px', marginTop: '10px' }}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Active Halts Summary Bar */}
        {Object.keys(haltedMap).length > 0 && (
          <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: '8px', marginTop: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: '#b45309', marginBottom: '6px' }}>
              <ShieldAlert size={15} />
              <span>Şu Anda Durdurulmuş Enstrümanlar ({Object.keys(haltedMap).length})</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {Object.entries(haltedMap).map(([sym, rsn]) => (
                <span
                  key={sym}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '3px 8px',
                    borderRadius: '4px',
                    background: '#fee2e2',
                    color: '#991b1b',
                    fontSize: '11px',
                    fontWeight: 500
                  }}
                  title={rsn}
                >
                  <strong>{sym}</strong>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleQuickResume(sym)}
                    style={{ background: 'none', border: 'none', color: '#991b1b', cursor: 'pointer', padding: 0 }}
                    title="Durdurmayı Kaldır"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
          {/* Instrument Search & Category Filters */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: 'var(--crm-muted)' }}>
                Enstrüman Seçin ({instruments.length} kayıt arasından)
              </label>
              <span style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>
                {filteredInstruments.length} eşleşen
              </span>
            </div>

            {/* Category Pills */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
              {Object.entries(CATEGORY_MAP).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCategory(key)}
                  style={{
                    padding: '3px 8px',
                    fontSize: '11px',
                    borderRadius: '4px',
                    border: category === key ? '1px solid #2563eb' : '1px solid var(--crm-line)',
                    background: category === key ? '#eff6ff' : 'var(--crm-surface)',
                    color: category === key ? '#1d4ed8' : 'var(--crm-ink)',
                    cursor: 'pointer'
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Live Search Input */}
            <div style={{ position: 'relative', marginBottom: '8px' }}>
              <Search size={14} style={{ position: 'absolute', left: '10px', top: '11px', color: 'var(--crm-muted)' }} />
              <input
                type="text"
                placeholder="Sembol veya isim ile ara (Örn: THYAO, AAPL, EURUSD, BTC, GOLD)..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', height: '36px', paddingLeft: '32px', paddingRight: '10px', border: '1px solid var(--crm-line)', borderRadius: '6px', background: 'var(--crm-surface)', color: 'var(--crm-ink)', fontSize: '13px' }}
              />
            </div>

            {/* Filtered Instruments Scrollable List */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '6px', maxHeight: '180px', overflowY: 'auto', padding: '8px', border: '1px solid var(--crm-line)', borderRadius: '6px', background: 'var(--crm-surface)' }}>
              {filteredInstruments.slice(0, 100).map(inst => {
                const isSelected = selectedSymbol === inst.symbol;
                const isHalted = Boolean(haltedMap[inst.symbol]);
                return (
                  <div
                    key={inst.symbol}
                    onClick={() => {
                      setSelectedSymbol(inst.symbol);
                      setHalted(!isHalted);
                    }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '6px 8px',
                      borderRadius: '4px',
                      border: isSelected ? '1.5px solid #2563eb' : '1px solid var(--crm-line)',
                      background: isSelected ? 'rgba(37,99,235,0.08)' : isHalted ? 'rgba(239,68,68,0.06)' : 'transparent',
                      cursor: 'pointer',
                      transition: 'all 0.1s ease'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: '12px', color: 'var(--crm-ink)' }}>{inst.symbol}</strong>
                      {isHalted ? (
                        <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '3px', background: '#fee2e2', color: '#991b1b', fontWeight: 600 }}>
                          Durduruldu
                        </span>
                      ) : (
                        <span style={{ fontSize: '10px', color: 'var(--crm-muted)' }}>
                          {inst.market_type || 'aktif'}
                        </span>
                      )}
                    </div>
                    {inst.name && (
                      <span style={{ fontSize: '11px', color: 'var(--crm-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {inst.name}
                      </span>
                    )}
                  </div>
                );
              })}
              {filteredInstruments.length === 0 && (
                <div style={{ gridColumn: '1 / -1', padding: '16px', textAlign: 'center', color: 'var(--crm-muted)', fontSize: '12px' }}>
                  Aramanızla eşleşen enstrüman bulunamadı.
                </div>
              )}
            </div>
          </div>

          {/* Selected Instrument & Action */}
          {selectedInstrument && (
            <div style={{ padding: '12px', borderRadius: '8px', border: '1px solid var(--crm-line)', background: 'var(--crm-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--crm-muted)', display: 'block' }}>Seçilen Enstrüman</span>
                  <strong style={{ fontSize: '14px', color: 'var(--crm-ink)' }}>{selectedInstrument.symbol}</strong>
                  <span style={{ fontSize: '12px', color: 'var(--crm-muted)', marginLeft: '6px' }}>
                    ({selectedInstrument.name || selectedInstrument.market_type})
                  </span>
                </div>
                <div>
                  {isCurrentlyHalted ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#dc2626', fontWeight: 600 }}>
                      <Pause size={14} /> ŞU AN DURDURULMUŞ
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#16a34a', fontWeight: 600 }}>
                      <Play size={14} /> ŞU AN AÇIK
                    </span>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <button
                  type="button"
                  onClick={() => setHalted(true)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '8px',
                    borderRadius: '6px',
                    border: halted ? '1.5px solid #dc2626' : '1px solid var(--crm-line)',
                    background: halted ? '#fee2e2' : 'var(--crm-surface)',
                    color: halted ? '#991b1b' : 'var(--crm-ink)',
                    fontWeight: 600,
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <Pause size={14} />
                  Yeni İşlemleri Durdur
                </button>
                <button
                  type="button"
                  onClick={() => setHalted(false)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '8px',
                    borderRadius: '6px',
                    border: !halted ? '1.5px solid #16a34a' : '1px solid var(--crm-line)',
                    background: !halted ? '#dcfce7' : 'var(--crm-surface)',
                    color: !halted ? '#166534' : 'var(--crm-ink)',
                    fontWeight: 600,
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <Play size={14} />
                  İşlemleri Başlat / Kaldır
                </button>
              </div>

              {/* Reason */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'var(--crm-muted)', marginBottom: '5px' }}>
                  Açıklama / Durdurma Gerekçesi
                </label>
                <input
                  type="text"
                  placeholder="Örn: Borsa devre kesici, olağandışı oynaklık, şirket haberi..."
                  value={reason}
                  disabled={busy}
                  onChange={e => setReason(e.target.value)}
                  style={{ width: '100%', height: '36px', padding: '6px 10px', border: '1px solid var(--crm-line)', borderRadius: '6px', background: 'var(--crm-surface)', color: 'var(--crm-ink)', fontSize: '13px' }}
                />
              </div>
            </div>
          )}

          {/* Dialog Actions */}
          <div className="crm-dialog-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px', paddingTop: '12px', borderTop: '1px solid var(--crm-line)' }}>
            <Button type="button" variant="outline" disabled={busy} onClick={onClose}>
              Vazgeç
            </Button>
            <Button type="submit" className="crm-primary" disabled={busy || !selectedSymbol} style={{ minWidth: '130px' }}>
              {busy ? 'İşleniyor…' : halted ? 'İşlemleri Durdur' : 'İşlemleri Başlat'}
            </Button>
          </div>
        </form>
      </CRMDialogContent>
    </Dialog>
  );
};
